import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Plus, Search, Pencil, Trash2, X, User, Phone, Mail, MapPin, AlertTriangle, Loader2, ShieldCheck, HardHat, UserCog, RotateCcw, KeyRound, Users } from 'lucide-react'
import { engineerService, type FieldEngineer, type FieldEngineerInput, type ManagedUserRole } from '@/services/firebase/engineers'
import { authService } from '@/services/firebase/auth'
import { useAuthStore } from '@/store/authStore'
import toast from 'react-hot-toast'

// ─── Form Modal ───────────────────────────────────────────────────────────────
interface EngineerFormProps {
  initial?: FieldEngineer | null
  onClose: () => void
  onSaved: () => void
}

const EMPTY_FORM: FieldEngineerInput = { name: '', email: '', phoneNumber: '', location: '', role: 'engineer', isActive: true }

const EngineerForm = ({ initial, onClose, onSaved }: EngineerFormProps) => {
  const [form, setForm] = useState<FieldEngineerInput>(
    initial
      ? { name: initial.name, email: initial.email, phoneNumber: initial.phoneNumber, location: initial.location, role: initial.role ?? 'engineer', isActive: initial.isActive }
      : EMPTY_FORM
  )
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof FieldEngineerInput, string>>>({})

  const validate = () => {
    const e: typeof errors = {}
    if (!form.name.trim()) e.name = 'Name is required'
    if (!form.phoneNumber.trim()) e.phoneNumber = 'Phone number is required'
    else if (!/^[6-9]\d{9}$/.test(form.phoneNumber.replace(/\s/g, ''))) e.phoneNumber = 'Enter a valid 10-digit mobile number'
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email address'
    if (!form.location.trim()) e.location = 'Location is required'
    return e
  }

  const handleSubmit = async () => {
    const e = validate()
    if (Object.keys(e).length) { setErrors(e); return }
    setSaving(true)
    try {
      const result = initial
        ? await engineerService.update(initial.id, form)
        : await engineerService.create(form)
      if (!result.success) throw new Error(result.error)
      toast.success(initial ? 'User updated!' : `${form.role === 'admin' ? 'Admin' : 'Field Engineer'} added successfully!`)
      onSaved()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  const field = (key: keyof FieldEngineerInput) => ({
    value: form[key] as string,
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm(f => ({ ...f, [key]: e.target.value }))
      setErrors(er => ({ ...er, [key]: undefined }))
    }
  })

  const iw = "flex items-center gap-2 border border-gray-200 rounded-2xl px-3.5 py-2.5 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 hover:border-gray-300 bg-white/90 transition-all shadow-sm"
  const ic = "flex-1 text-sm outline-none text-gray-900 placeholder-gray-400 bg-transparent"

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}>
      <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl overflow-hidden"
        style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}>

        {/* Coloured header band */}
        <div className="px-5 py-4 flex items-center justify-between"
          style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 100%)' }}>
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-2xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, #3b82f6, #06b6d4)', boxShadow: '0 0 12px rgba(59,130,246,0.4)' }}>
              <UserCog size={16} className="text-white" />
            </div>
            <div>
              <h2 className="font-extrabold text-white text-sm">{initial ? 'Edit User' : 'Add New User'}</h2>
              <p className="text-[10px] text-blue-300/70">{initial ? 'Update user details' : 'Fill in the details below'}</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-xl bg-white/10 hover:bg-white/20 text-white transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Role selector */}
          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-2">Role *</label>
            <div className="flex gap-2">
              {(['engineer', 'admin'] as ManagedUserRole[]).map(r => (
                <button key={r} type="button"
                  onClick={() => setForm(f => ({ ...f, role: r }))}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-2xl border-2 text-sm font-bold transition-all duration-150 ${
                    form.role === r
                      ? r === 'admin'
                        ? 'bg-purple-50 border-purple-400 text-purple-700 shadow-sm'
                        : 'bg-blue-50 border-blue-400 text-blue-700 shadow-sm'
                      : 'bg-white border-gray-200 text-gray-400 hover:border-gray-300'
                  }`}>
                  {r === 'admin' ? <ShieldCheck size={15} /> : <HardHat size={15} />}
                  {r === 'admin' ? 'Admin' : 'Field Engineer'}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-gray-400 mt-1.5">
              {form.role === 'admin'
                ? '🛡 Admin can manage tasks, users and all settings'
                : '⚙ Field engineer can view tasks and update job status'}
            </p>
          </div>

          {/* Name */}
          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Full Name *</label>
            <div className={iw}>
              <User size={14} className="text-gray-400 shrink-0" />
              <input {...field('name')} placeholder="e.g. Ravi Kumar" className={ic} />
            </div>
            {errors.name && <p className="text-xs text-red-500 mt-1 font-medium">{errors.name}</p>}
          </div>

          {/* Phone */}
          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Mobile Number *</label>
            <div className={iw}>
              <Phone size={14} className="text-gray-400 shrink-0" />
              <input {...field('phoneNumber')} type="tel" inputMode="numeric" placeholder="98765 43210" className={ic} />
            </div>
            {errors.phoneNumber && <p className="text-xs text-red-500 mt-1 font-medium">{errors.phoneNumber}</p>}
          </div>

          {/* Email */}
          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Email ID <span className="text-gray-300 font-medium normal-case tracking-normal">(optional)</span></label>
            <div className={iw}>
              <Mail size={14} className="text-gray-400 shrink-0" />
              <input {...field('email')} type="email" placeholder="ravi@bsnl.in" className={ic} />
            </div>
            {errors.email && <p className="text-xs text-red-500 mt-1 font-medium">{errors.email}</p>}
          </div>

          {/* Location */}
          <div>
            <label className="block text-[11px] font-black text-gray-400 uppercase tracking-wider mb-1.5">Location / Zone *</label>
            <div className={iw}>
              <MapPin size={14} className="text-gray-400 shrink-0" />
              <input {...field('location')} placeholder="e.g. Kozhikode North" className={ic} />
            </div>
            {errors.location && <p className="text-xs text-red-500 mt-1 font-medium">{errors.location}</p>}
          </div>

          {/* Active toggle */}
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-2xl border border-gray-100">
            <div>
              <p className="text-sm text-gray-800 font-semibold">Active</p>
              <p className="text-[11px] text-gray-400">User can log in and use the app</p>
            </div>
            <button type="button"
              onClick={() => setForm(f => ({ ...f, isActive: !f.isActive }))}
              className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${form.isActive ? 'bg-blue-600' : 'bg-gray-300'}`}>
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow-sm transition-transform duration-200 ${form.isActive ? 'translate-x-6' : 'translate-x-0'}`} />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 pb-5 flex gap-3">
          <button onClick={onClose} className="flex-1 py-3 border border-gray-200 rounded-2xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={saving}
            className="flex-2 flex-1 py-3 text-white rounded-2xl text-sm font-black transition-all disabled:opacity-60 flex items-center justify-center gap-2 active:scale-[0.98]"
            style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)', boxShadow: '0 4px 16px rgba(37,99,235,0.3)' }}>
            {saving ? <Loader2 size={15} className="animate-spin" /> : null}
            {saving ? 'Saving…' : initial ? 'Save Changes' : `Add ${form.role === 'admin' ? 'Admin' : 'Engineer'}`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ─── Reset Password Confirm ──────────────────────────────────────────────────
interface ResetPwdConfirmProps {
  eng: FieldEngineer
  onConfirm: () => void
  onCancel: () => void
  resetting: boolean
}

const ResetPwdConfirm = ({ eng, onConfirm, onCancel, resetting }: ResetPwdConfirmProps) => createPortal(
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}>
    <div className="bg-white rounded-3xl w-full max-w-sm p-7 flex flex-col items-center text-center gap-5"
      style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}>
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg,rgba(124,58,237,0.1),rgba(99,102,241,0.05))', border: '1px solid rgba(124,58,237,0.2)' }}>
        <KeyRound size={28} className="text-violet-600" />
      </div>
      <div>
        <p className="font-extrabold text-gray-900 text-lg">Reset Password?</p>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed">
          <span className="font-bold text-gray-800">{eng.name}</span>'s password will be reset to{' '}
          <span className="font-black text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">123456</span>.
          <br />They will be asked to set a new password on next login.
        </p>
      </div>
      <div className="flex gap-3 w-full">
        <button onClick={onCancel} className="flex-1 py-3 border border-gray-200 rounded-2xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={resetting}
          className="flex-1 py-3 text-white rounded-2xl text-sm font-black transition-all disabled:opacity-60 flex items-center justify-center gap-2 active:scale-[0.98]"
          style={{ background: 'linear-gradient(135deg,#7c3aed,#6366f1)', boxShadow: '0 4px 16px rgba(124,58,237,0.3)' }}>
          {resetting ? <Loader2 size={14} className="animate-spin" /> : <RotateCcw size={14} />}
          {resetting ? 'Resetting…' : 'Reset Password'}
        </button>
      </div>
    </div>
  </div>,
  document.body
)

// ─── Delete Confirm ───────────────────────────────────────────────────────────
interface DeleteConfirmProps {
  name: string
  onConfirm: () => void
  onCancel: () => void
  deleting: boolean
}

const DeleteConfirm = ({ name, onConfirm, onCancel, deleting }: DeleteConfirmProps) => createPortal(
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
    style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)' }}>
    <div className="bg-white rounded-3xl w-full max-w-sm p-7 flex flex-col items-center text-center gap-5"
      style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.25)' }}>
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center"
        style={{ background: 'linear-gradient(135deg, rgba(239,68,68,0.1), rgba(239,68,68,0.05))', border: '1px solid rgba(239,68,68,0.2)' }}>
        <AlertTriangle size={28} className="text-red-500" />
      </div>
      <div>
        <p className="font-extrabold text-gray-900 text-lg">Delete User?</p>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed">
          <span className="font-bold text-gray-800">{name}</span> will be permanently removed.<br />This action cannot be undone.
        </p>
      </div>
      <div className="flex gap-3 w-full">
        <button onClick={onCancel} className="flex-1 py-3 border border-gray-200 rounded-2xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition-colors">
          Cancel
        </button>
        <button onClick={onConfirm} disabled={deleting}
          className="flex-1 py-3 text-white rounded-2xl text-sm font-black transition-all disabled:opacity-60 flex items-center justify-center gap-2 active:scale-[0.98]"
          style={{ background: 'linear-gradient(135deg, #ef4444, #dc2626)', boxShadow: '0 4px 16px rgba(239,68,68,0.3)' }}>
          {deleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
          {deleting ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    </div>
  </div>,
  document.body
)

// ─── Main Page ────────────────────────────────────────────────────────────────
const EngineerListPage = () => {
  const { user } = useAuthStore()
  const isAdmin = user?.role === 'admin'

  const [engineers, setEngineers] = useState<FieldEngineer[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showForm, setShowForm] = useState(false)
  const [editTarget, setEditTarget] = useState<FieldEngineer | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<FieldEngineer | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [resetTarget, setResetTarget] = useState<FieldEngineer | null>(null)
  const [resetting, setResetting] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    const res = await engineerService.list()
    if (res.success && res.data) setEngineers(res.data)
    else toast.error(res.error ?? 'Failed to load engineers')
    setLoading(false)
  }, [])

  useEffect(() => { load() }, [load])

  const filtered = engineers.filter(e =>
    e.name.toLowerCase().includes(search.toLowerCase()) ||
    e.phoneNumber.includes(search) ||
    e.location.toLowerCase().includes(search.toLowerCase())
  )

  const handleResetPassword = async () => {
    if (!resetTarget) return
    setResetting(true)
    try {
      await authService.adminResetPassword(resetTarget.email, resetTarget.uid)
      toast.success(`Password reset to 123456 for ${resetTarget.name}`)
      setResetTarget(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Reset failed')
    }
    setResetting(false)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    const res = await engineerService.remove(deleteTarget.id)
    if (res.success) {
      toast.success('User deleted')
      setDeleteTarget(null)
      load()
    } else {
      toast.error(res.error ?? 'Delete failed')
    }
    setDeleting(false)
  }

  const adminCount = engineers.filter(e => e.role === 'admin').length
  const engCount = engineers.filter(e => e.role !== 'admin').length

  return (
    <div className="space-y-5 animate-page-enter">

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-3xl text-white"
        style={{ background: 'linear-gradient(135deg,#050d1f 0%,#0c1e3d 50%,#1a0535 100%)' }}>
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(139,92,246,0.35) 0%,transparent 70%)', filter: 'blur(40px)' }} />
          <div className="absolute bottom-0 left-1/3 w-40 h-28 rounded-full"
            style={{ background: 'radial-gradient(circle,rgba(37,99,235,0.2) 0%,transparent 70%)', filter: 'blur(30px)' }} />
        </div>
        <div className="relative z-10 p-5 flex items-center justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 mb-2">
              <Users size={10} className="text-purple-300" />
              <span className="text-[10px] text-blue-200 font-semibold uppercase tracking-wider">Team Management</span>
            </div>
            <h1 className="text-[22px] font-black leading-tight">Users</h1>
            <p className="text-blue-300/70 text-sm mt-0.5">
              {engineers.length} total
              {adminCount > 0 && <> · <span className="text-purple-300 font-bold">{adminCount} admin{adminCount !== 1 ? 's' : ''}</span></>}
              {engCount > 0 && <> · <span className="text-cyan-300 font-bold">{engCount} engineer{engCount !== 1 ? 's' : ''}</span></>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="flex items-center gap-2 px-3 py-2 rounded-2xl text-[12px] font-semibold"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.1)' }}>
              {isAdmin ? <ShieldCheck size={13} className="text-purple-300" /> : <HardHat size={13} className="text-cyan-300" />}
              <span className="text-white/70">{isAdmin ? 'Admin' : 'Engineer'}</span>
            </div>
            {isAdmin && (
              <button
                onClick={() => { setEditTarget(null); setShowForm(true) }}
                className="flex items-center gap-1.5 px-3.5 py-2 text-white rounded-2xl text-[13px] font-black transition-all active:scale-95"
                style={{ background: 'linear-gradient(135deg,#7c3aed,#6366f1)', boxShadow: '0 4px 14px rgba(124,58,237,0.4)' }}
              >
                <Plus size={14} /> Add
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-2xl px-4 py-2.5 focus-within:ring-2 focus-within:ring-blue-500/20 focus-within:border-blue-400 shadow-sm transition-all">
        <Search size={14} className="text-gray-400 shrink-0" />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search by name, phone or location…"
          className="flex-1 text-sm outline-none text-gray-900 placeholder-gray-400 bg-transparent"
        />
        {search && <button onClick={() => setSearch('')}><X size={13} className="text-gray-400 hover:text-gray-600" /></button>}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <div className="w-12 h-12 rounded-2xl flex items-center justify-center"
            style={{ background: 'linear-gradient(135deg, rgba(37,99,235,0.08), rgba(6,182,212,0.08))' }}>
            <Loader2 size={22} className="text-blue-500 animate-spin" />
          </div>
          <p className="text-sm text-gray-400 font-medium">Loading users…</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center">
            <UserCog size={28} className="text-gray-300" />
          </div>
          <div>
            <p className="font-bold text-gray-700">{search ? 'No users match your search' : 'No users added yet'}</p>
            <p className="text-sm text-gray-400 mt-1">{search ? 'Try a different keyword' : 'Add your first admin or engineer'}</p>
          </div>
          {!search && isAdmin && (
            <button onClick={() => { setEditTarget(null); setShowForm(true) }}
              className="px-5 py-2.5 text-sm font-bold text-white rounded-xl"
              style={{ background: 'linear-gradient(135deg, #2563eb, #1d4ed8)' }}>
              + Add User
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((eng, i) => (
            <div key={eng.id} className="card-premium p-4 group animate-card-enter" style={{ '--stagger': i } as React.CSSProperties}>
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="w-12 h-12 rounded-2xl shrink-0 flex items-center justify-center text-white font-black text-base shadow-sm"
                  style={{ background: eng.role === 'admin'
                    ? 'linear-gradient(135deg, #7c3aed, #6366f1)'
                    : 'linear-gradient(135deg, #2563eb, #06b6d4)' }}>
                  {eng.name.charAt(0).toUpperCase()}
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1.5">
                    <p className="font-bold text-gray-900 text-sm">{eng.name}</p>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-black shrink-0 ${
                      eng.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {eng.role === 'admin' ? '🛡 Admin' : '⚙ Engineer'}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold shrink-0 ${eng.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                      {eng.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <Phone size={11} className="shrink-0 text-gray-400" /><span>{eng.phoneNumber}</span>
                    </div>
                    {eng.email && (
                      <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <Mail size={11} className="shrink-0 text-gray-400" /><span className="truncate">{eng.email}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                      <MapPin size={11} className="shrink-0 text-gray-400" /><span>{eng.location}</span>
                    </div>
                  </div>
                </div>
                {/* Actions */}
                {isAdmin && (
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button onClick={() => { setEditTarget(eng); setShowForm(true) }}
                      className="w-8 h-8 flex items-center justify-center rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-600 transition-colors"
                      title="Edit user">
                      <Pencil size={13} />
                    </button>
                    {eng.email && (
                      <button onClick={() => setResetTarget(eng)}
                        className="w-8 h-8 flex items-center justify-center rounded-xl bg-violet-50 hover:bg-violet-100 text-violet-600 transition-colors"
                        title="Reset password to 123456">
                        <RotateCcw size={13} />
                      </button>
                    )}
                    <button onClick={() => setDeleteTarget(eng)}
                      className="w-8 h-8 flex items-center justify-center rounded-xl bg-red-50 hover:bg-red-100 text-red-500 transition-colors"
                      title="Delete user">
                      <Trash2 size={13} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showForm && (
        <EngineerForm
          initial={editTarget}
          onClose={() => { setShowForm(false); setEditTarget(null) }}
          onSaved={() => { setShowForm(false); setEditTarget(null); load() }}
        />
      )}
      {deleteTarget && (
        <DeleteConfirm
          name={deleteTarget.name}
          deleting={deleting}
          onConfirm={handleDelete}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
      {resetTarget && (
        <ResetPwdConfirm
          eng={resetTarget}
          resetting={resetting}
          onConfirm={handleResetPassword}
          onCancel={() => setResetTarget(null)}
        />
      )}
    </div>
  )
}

export default EngineerListPage
